import {
	Component,
	inject,
	OnInit,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
	GameColorPalette,
	GameCustomizationService,
	DynamicModalWrapperService,
	RendererGameDefinitionService,
	PluginType,
	ColorPurpose,
	SingleColor,
} from '@meta-press/shared-plugins';
import { ColorPalettePreviewComponent } from '../color-palette-preview/color-palette-preview.component';
import { ColorPaletteEditorComponent } from '../color-palette-editor/color-palette-editor.component';
import { SettingsService } from '../../services/settings-service/settings.service';

/**
 * Component for managing all saved color palettes
 * Allows viewing, editing, and deleting palettes organized by visibility scope
 */
@Component({
	selector: 'app-color-palette-management',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ColorPalettePreviewComponent,
		ColorPaletteEditorComponent,
	],
	templateUrl: './color-palette-management.component.html',
	styleUrl: './color-palette-management.component.scss',
})
export class ColorPaletteManagementComponent implements OnInit {
	// Inject services
	private gameCustomizationService = inject(GameCustomizationService);
	private modalService = inject(NgbModal);
	private dynamicModalService = inject(DynamicModalWrapperService);
	private rendererGameDefinitionService = inject(
		RendererGameDefinitionService,
	);
	private settingsService = inject(SettingsService);
	public activeModal = inject(NgbActiveModal);

	// Palette categories
	allGamesPalettes: GameColorPalette[] = [];
	gameSpecificPalettes: GameColorPalette[] = [];
	interfaceSpecificPalettes: GameColorPalette[] = [];

	// Editing state
	editingPalette: GameColorPalette | null = null;
	editingPaletteVisibility: 'all' | 'game' | 'interface' = 'interface';
	private editModalRef: any = null;

	// Available plugin types for dropdown
	availablePluginTypes: Array<{
		type: PluginType;
		title: string;
		description: string;
	}> = [];

	// Reference to edit modal template
	@ViewChild('editPaletteModal') editPaletteModalRef!: TemplateRef<any>;

	// Reference to delete confirmation modal template
	@ViewChild('deletePaletteModal') deletePaletteModalRef!: TemplateRef<any>;

	// Palette pending deletion (for confirmation modal)
	paletteToDelete: GameColorPalette | null = null;

	ngOnInit(): void {
		this.loadPalettes();
		this.loadAvailablePluginTypes();
	}

	/**
	 * Load available plugin types filtered by environment settings
	 * - Filters by production state (Production, Beta, or Internal)
	 * - Sorted alphabetically by title (Finnish locale)
	 */
	private loadAvailablePluginTypes(): void {
		this.availablePluginTypes =
			this.settingsService.getFilteredPluginTypes();
	}

	/**
	 * Check if plugin type dropdown should be shown
	 */
	shouldShowPluginTypeDropdown(): boolean {
		return (
			this.editingPaletteVisibility === 'interface' ||
			this.editingPaletteVisibility === 'game'
		);
	}

	/**
	 * Get the selected plugin type display name
	 */
	getSelectedPluginTypeName(): string {
		if (!this.editingPalette?.pluginType) {
			return 'Valitse peli...';
		}
		const pluginType = this.availablePluginTypes.find(
			(pt) => pt.type === this.editingPalette?.pluginType,
		);
		return pluginType?.title || 'Tuntematon';
	}

	/**
	 * Select a plugin type from the dropdown
	 * @deprecated Use togglePluginType instead
	 */
	selectPluginType(pluginType: PluginType): void {
		if (this.editingPalette) {
			this.editingPalette.pluginType = pluginType;
		}
	}

	/**
	 * Toggle plugin type selection (for pills UI)
	 * For 'game' visibility: multiple selection (checkbox behavior)
	 */
	togglePluginType(pluginType: PluginType): void {
		if (!this.editingPalette) return;

		// Initialize pluginTypes array if it doesn't exist
		if (!this.editingPalette.pluginTypes) {
			this.editingPalette.pluginTypes = [];
		}

		// Multiple selection for 'game' visibility
		const index = this.editingPalette.pluginTypes.indexOf(pluginType);
		if (index >= 0) {
			// Remove if already selected
			this.editingPalette.pluginTypes.splice(index, 1);
		} else {
			// Add to selection
			this.editingPalette.pluginTypes.push(pluginType);
		}
	}

	/**
	 * Check if a plugin type is currently selected
	 */
	isPluginTypeSelected(pluginType: PluginType): boolean {
		if (!this.editingPalette?.pluginTypes) {
			return false;
		}
		return this.editingPalette.pluginTypes.includes(pluginType);
	}

	/**
	 * Get display name for a plugin type
	 */
	getPluginTypeName(pluginType: PluginType): string {
		const pt = this.availablePluginTypes.find((p) => p.type === pluginType);
		return pt?.title || 'Tuntematon';
	}

	/**
	 * Check if plugin type selector should be shown (only for 'game' visibility)
	 */
	shouldShowPluginTypeSelector(): boolean {
		return this.editingPaletteVisibility === 'game';
	}

	/**
	 * Check if interface info should be shown (for 'interface' visibility)
	 */
	shouldShowInterfaceInfo(): boolean {
		return this.editingPaletteVisibility === 'interface';
	}

	/**
	 * Get game and interface info for interface-specific palettes
	 */
	getGameInfoForInterface(): { gameName: string; interfaceName: string } {
		if (!this.editingPalette) {
			return { gameName: 'Tuntematon', interfaceName: 'Tuntematon' };
		}

		// Extract info from palette ID (format: pluginType-interfaceIndex-timestamp-random)
		const idParts = this.editingPalette.id.split('-');
		const pluginType = idParts[0] ? parseInt(idParts[0]) : undefined;
		const interfaceIndex = idParts[1];

		// Get game name from plugin type
		let gameName = 'Tuntematon peli';
		if (pluginType !== undefined) {
			const pt = this.availablePluginTypes.find(
				(p) => p.type === pluginType,
			);
			gameName = pt?.title || 'Tuntematon peli';
		}

		// Interface name is typically just the index
		const interfaceName = interfaceIndex
			? `Ulkoasu ${interfaceIndex + 1}`
			: 'Tuntematon ulkoasu';

		return { gameName, interfaceName };
	}

	/**
	 * Create a deep copy of a palette including its colors Map
	 * @param palette The palette to copy
	 * @returns A deep copy of the palette
	 */
	private deepCopyPalette(palette: GameColorPalette): GameColorPalette {
		const colorsCopy = new Map<ColorPurpose, SingleColor>();
		for (const [purpose, color] of palette.colors.entries()) {
			colorsCopy.set(purpose, new SingleColor(color.color));
		}

		return {
			...palette,
			colors: colorsCopy,
		};
	}

	/**
	 * Load all palettes and sort them by visibility
	 */
	private loadPalettes(): void {
		// Get all saved palettes without filtering
		const allPalettes = this.gameCustomizationService.getSavedPalettes();

		// Sort into categories based on visibility
		this.allGamesPalettes = allPalettes.filter(
			(p) => (p.visibility || 'interface') === 'all',
		);
		this.gameSpecificPalettes = allPalettes.filter(
			(p) => (p.visibility || 'interface') === 'game',
		);
		this.interfaceSpecificPalettes = allPalettes.filter(
			(p) => (p.visibility || 'interface') === 'interface',
		);
	}

	/**
	 * Refresh the palette list after changes
	 */
	refreshPalettes(): void {
		this.loadPalettes();
	}

	/**
	 * Open the color palette editor for a specific palette
	 * @param palette The palette to edit
	 */
	editPalette(palette: GameColorPalette): void {
		// Create a deep copy for editing (including colors Map)
		this.editingPalette = this.deepCopyPalette(palette);

		// Migration: Initialize pluginTypes array from old pluginType if needed
		if (!this.editingPalette.pluginTypes) {
			if (this.editingPalette.pluginType) {
				this.editingPalette.pluginTypes = [
					this.editingPalette.pluginType,
				];
			} else {
				this.editingPalette.pluginTypes = [];
			}
		}

		this.editingPaletteVisibility = (palette.visibility || 'interface') as
			| 'all'
			| 'game'
			| 'interface';

		// Open the custom modal and store the reference
		this.editModalRef = this.modalService.open(this.editPaletteModalRef, {
			size: 'xl',
			backdrop: 'static',
			keyboard: false,
		});
	}

	/**
	 * Handle palette changes from the editor
	 */
	onPaletteChange(updatedPalette: GameColorPalette): void {
		// Update the editing palette with the new values (including colors Map)
		if (this.editingPalette) {
			this.editingPalette = this.deepCopyPalette(updatedPalette);
		}
	}

	/**
	 * Save the edited palette
	 */
	saveEditedPalette(): void {
		if (this.editingPalette) {
			// Update visibility from dropdown
			this.editingPalette.visibility = this.editingPaletteVisibility;

			// Save to localStorage
			this.gameCustomizationService.savePaletteToLocalStorage(
				this.editingPalette,
			);

			// Close the edit modal
			if (this.editModalRef) {
				this.editModalRef.close();
				this.editModalRef = null;
			}

			this.refreshPalettes();
			this.editingPalette = null;
		}
	}

	/**
	 * Cancel editing
	 */
	cancelEdit(): void {
		// Close the edit modal, not the parent modal
		if (this.editModalRef) {
			this.editModalRef.close();
			this.editModalRef = null;
		}
		this.editingPalette = null;
	}

	/**
	 * Show delete confirmation modal for a palette
	 * @param palette The palette to delete
	 * @param modalTemplate The modal template reference
	 */
	confirmDeletePalette(
		palette: GameColorPalette,
		modalTemplate: TemplateRef<any>,
	): void {
		// Store the palette to be deleted so the modal can display its name
		this.paletteToDelete = palette;

		const modalRef = this.modalService.open(modalTemplate, {
			centered: true,
		});

		modalRef.result.then(
			(result) => {
				if (result === 'confirm') {
					this.performDelete();
				}
				this.paletteToDelete = null;
			},
			() => {
				// Modal dismissed
				this.paletteToDelete = null;
			},
		);
	}

	/**
	 * Actually delete the palette (called after confirmation)
	 */
	private performDelete(): void {
		if (this.paletteToDelete) {
			// Delete the palette
			this.gameCustomizationService.deletePalette(
				this.paletteToDelete.id,
			);
			// Refresh the palette list
			this.refreshPalettes();
		}
	}

	/**
	 * Check if a category has palettes
	 * @param category The category to check ('all', 'game', or 'interface')
	 */
	hasPalettes(category: 'all' | 'game' | 'interface'): boolean {
		switch (category) {
			case 'all':
				return this.allGamesPalettes.length > 0;
			case 'game':
				return this.gameSpecificPalettes.length > 0;
			case 'interface':
				return this.interfaceSpecificPalettes.length > 0;
		}
	}

	/**
	 * Close the modal
	 */
	close(): void {
		this.activeModal.close();
	}

	/**
	 * Track by function for palette list performance
	 */
	trackByPaletteId(_index: number, palette: GameColorPalette): string {
		return palette.id;
	}

	/**
	 * Get game names associated with a palette
	 * - For 'all' visibility: returns empty array (applies to all games)
	 * - For 'game' visibility: returns list of game titles from pluginTypes
	 * - For 'interface' visibility: returns game name extracted from ID
	 */
	getGameNamesForPalette(palette: GameColorPalette): string[] {
		const visibility = palette.visibility || 'interface';

		// Don't show game names for 'all' visibility palettes
		if (visibility === 'all') {
			return [];
		}

		// For 'game' visibility, show list of associated games
		if (visibility === 'game') {
			const types =
				palette.pluginTypes ||
				(palette.pluginType ? [palette.pluginType] : []);
			return types
				.map((type) => {
					const pt = this.availablePluginTypes.find(
						(p) => p.type === type,
					);
					return pt?.title || `Peli ${type}`;
				})
				.sort((a, b) => a.localeCompare(b, 'fi'));
		}

		// For 'interface' visibility, extract game name from ID
		if (visibility === 'interface') {
			const idParts = palette.id.split('-');
			const pluginType = idParts[0] ? parseInt(idParts[0]) : undefined;

			if (pluginType !== undefined) {
				const pt = this.availablePluginTypes.find(
					(p) => p.type === pluginType,
				);
				if (pt) {
					const interfaceIndex = idParts[1];
					return [
						`${pt.title} - Ulkoasu ${parseInt(interfaceIndex) + 1}`,
					];
				}
			}
		}

		return [];
	}
}
